// @ts-check
// One popover names the hovered card swatch for the whole page. It sits in the top layer because
// the swatch row clips vertically (overflow-list) and a card can sit flush on the next row.
const SWATCH = '[data-custom-swatch-tooltip] .variant-option__swatch label';
const EDGE = 8;

/** @type {HTMLElement | null} */
let tooltip = null;
/** @type {HTMLLabelElement | null} */
let shown = null;

/**
 * @param {EventTarget | null} target
 * @returns {HTMLLabelElement | null}
 */
function swatchFrom(target) {
  return target instanceof Element ? target.closest(SWATCH) : null;
}

/** @param {PointerEvent} event */
function show(event) {
  if (event.pointerType !== 'mouse') return;

  const label = swatchFrom(event.target);
  const name = label?.querySelector('input')?.getAttribute('aria-label');
  if (!label || !name) return;

  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.className = 'custom-swatch-tooltip';
    tooltip.setAttribute('popover', 'manual');
    tooltip.setAttribute('aria-hidden', 'true');
    document.body.append(tooltip);
  }

  const chip = (label.querySelector('.swatch') ?? label).getBoundingClientRect();
  tooltip.textContent = name;
  tooltip.style.setProperty('--custom-swatch-tooltip-y', `${chip.bottom}px`);
  tooltip.removeAttribute('data-above');
  shown = label;
  if (!tooltip.matches(':popover-open')) tooltip.showPopover();

  // Under the swatch unless that runs below the screen; above, the cursor cannot cover it.
  if (tooltip.getBoundingClientRect().bottom > document.documentElement.clientHeight - EDGE) {
    tooltip.style.setProperty('--custom-swatch-tooltip-y', `${chip.top}px`);
    tooltip.setAttribute('data-above', '');
  }

  // A long name over an edge swatch would run off screen, so pin it inside the viewport.
  const half = tooltip.offsetWidth / 2 + EDGE;
  const center = chip.left + chip.width / 2;
  const x = Math.max(half, Math.min(center, document.documentElement.clientWidth - half));
  tooltip.style.setProperty('--custom-swatch-tooltip-x', `${x}px`);
}

function hide() {
  shown = null;
  if (tooltip?.matches(':popover-open')) tooltip.hidePopover();
}

document.addEventListener('pointerover', show);
// Hover content has to be dismissible without moving the pointer (WCAG 1.4.13).
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && shown) hide();
});
document.addEventListener('pointerout', (event) => {
  const label = swatchFrom(event.target);
  if (label && !(event.relatedTarget instanceof Node && label.contains(event.relatedTarget)))
    hide();
});
// Only a scroll that moves the swatch hides it: hovering a swatch scrolls the card's own
// slideshow to that variant's photo, which must not close the name it just opened.
document.addEventListener(
  'scroll',
  (event) => {
    const scroller = event.target;
    if (scroller === document || (scroller instanceof Node && shown && scroller.contains(shown)))
      hide();
  },
  { capture: true, passive: true }
);
